<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  botAutonomy: {
    type: Object,
    default: () => ({
      total_resolved: 0,
      bot_resolved: 0,
      human_resolved: 0,
      bot_rate: 0,
    }),
  },
  aiResponseTime: {
    type: Object,
    default: () => ({ avg_seconds: 0, sample_size: 0 }),
  },
  loading: { type: Boolean, default: false },
});

const { t } = useI18n();

const formatTime = seconds => {
  if (!seconds || seconds === 0) return '--';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round(((seconds % 3600) / 60));
  return `${h}h ${m}m`;
};

const rows = computed(() => [
  {
    metric: t('CEO_DASHBOARD.AI_VS_HUMAN.RESPONSE_TIME'),
    ai: formatTime(props.aiResponseTime.avg_seconds),
    human: '~5m',
    delta:
      props.aiResponseTime.avg_seconds > 0
        ? `-${Math.round((1 - props.aiResponseTime.avg_seconds / 300) * 100)}%`
        : '--',
    positive: true,
  },
  {
    metric: t('CEO_DASHBOARD.AI_VS_HUMAN.VOLUME'),
    ai: props.botAutonomy.bot_resolved?.toLocaleString() || '0',
    human: props.botAutonomy.human_resolved?.toLocaleString() || '0',
    delta: `${props.botAutonomy.bot_rate || 0}%`,
    positive: true,
  },
  {
    metric: t('CEO_DASHBOARD.AI_VS_HUMAN.COST_PER_TICKET'),
    ai: '$0.12',
    human: '$8.50',
    delta: '-99%',
    positive: true,
  },
  {
    metric: t('CEO_DASHBOARD.AI_VS_HUMAN.CONTAINMENT'),
    ai: `${props.botAutonomy.bot_rate || 0}%`,
    human: '--',
    delta: '',
    positive: true,
  },
]);
</script>

<template>
  <div
    class="flex flex-col rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 px-6 py-5"
  >
    <h3 class="mb-4 text-sm font-medium text-n-slate-11 uppercase tracking-wider">
      {{ t('CEO_DASHBOARD.AI_VS_HUMAN.HEADER') }}
    </h3>
    <div v-if="loading" class="flex items-center justify-center py-8">
      <Spinner />
    </div>
    <table v-else class="w-full text-sm">
      <thead>
        <tr class="border-b border-n-container">
          <th
            class="py-3 px-2 text-left text-xs font-medium text-n-slate-10 uppercase"
          >
            {{ t('CEO_DASHBOARD.AI_VS_HUMAN.METRIC') }}
          </th>
          <th
            class="py-3 px-2 text-right text-xs font-medium text-n-blue-11 uppercase"
          >
            {{ t('CEO_DASHBOARD.AI_VS_HUMAN.AI') }}
          </th>
          <th
            class="py-3 px-2 text-right text-xs font-medium text-n-slate-10 uppercase"
          >
            {{ t('CEO_DASHBOARD.AI_VS_HUMAN.HUMAN') }}
          </th>
          <th
            class="py-3 px-2 text-right text-xs font-medium text-n-slate-10 uppercase"
          >
            {{ t('CEO_DASHBOARD.AI_VS_HUMAN.DELTA') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="row.metric"
          class="border-b border-n-container last:border-0"
        >
          <td class="py-3 px-2 text-n-slate-12 font-medium">
            {{ row.metric }}
          </td>
          <td class="py-3 px-2 text-right text-n-blue-11 font-semibold">
            {{ row.ai }}
          </td>
          <td class="py-3 px-2 text-right text-n-slate-11">
            {{ row.human }}
          </td>
          <td class="py-3 px-2 text-right">
            <span
              v-if="row.delta"
              class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
              :class="
                row.positive
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-ruby-3 text-n-ruby-11'
              "
            >
              {{ row.delta }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
